import React from 'react';
import {
  Table,
  TableBody,
  DataTableCell,
} from '@david.kucsai/react-pdf-table';
import { Text, View } from '@react-pdf/renderer';
import { lightGray, styles } from './pdfStyles';

const balanceSheetStyles = { 
  container: {
    marginTop: '10mm',
    marginBottom: '10mm'
  },
  title: {
    ...styles.h2,
    marginBottom: '2mm'
  },
  tableHead: {
    borderWidth: '0mm',
    backgroundColor: lightGray,
    fontSize: 6,
    fontWeight: 600,
  },
  tableCell: {
    fontSize: 6,
    paddingTop: '0.2mm',
    paddingBottom: '0.2mm',
    borderWidth: '0mm',
    // borderBottom: `1mm solid ${lightGray}`
  },
  tableCellRight: {
    borderWidth: '0mm',
    fontSize: 6,
    textAlign: 'right',
    paddingTop: '0.2mm',
    paddingBottom: '0.2mm',
    paddingLeft: '1mm'
  },
  tableCellIndent1: {
    borderWidth: '0mm',
    fontSize: 6,
    paddingLeft: '8mm',
    paddingTop: '0.2mm',
    paddingBottom: '0.2mm'
  },
  tableCellIndent2: {
    borderWidth: '0mm',
    fontSize: 6,
    paddingLeft: '12mm',
    paddingTop: '0.2mm',
    paddingBottom: '0.2mm'
  }
};

const MONTH_NAMES_PDF = [
  'JANEIRO',
  'FEVEREIRO',
  'MARÇO',
  'ABRIL',
  'MAIO',
  'JUNHO',
  'JULHO',
  'AGOSTO',
  'SETEMBRO',
  'OUTUBRO',
  'NOVEMBRO',
  'DEZEMBRO'
];

const formatCurrencyPdf = (amount) => {
  if (amount === null || amount === undefined) return 'R$ 0,00';
  const formatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return formatter.format(amount);
};

const cleanCategoryLabel = (label) => {
  if (!label) return '';
  return label.replace(/^\d+(?:\.\d+)*(?:[).\s-]*)/, '').trim();
};

const getMonthYearLabelPdf = (month, year) => {
  if (!month || !year) return '';
  const monthName = MONTH_NAMES_PDF[month - 1] || '';
  return `${monthName}/${year % 100}`;
};

const buildPdfBalanceSheetData = (balance) => {
  const data = [];

  if (!balance) return data;

  const {
    bankAccount,
    incomeBalanceAmount = 0,
    expenseBalanceAmount = 0,
    transferBalanceAmount = 0,
    monthBalanceAmount = 0,
    year,
    month,
    categories = []
  } = balance;

  // Header row with bank account and month/year
  const bankAccountName = bankAccount?.name || 'Conta Corrente';
  const monthYearLabel = getMonthYearLabelPdf(month, year);
  data.push({ item: bankAccountName, value: monthYearLabel, isHeader: false });

  // Calculate initial balance
  const initialBalance = monthBalanceAmount - (incomeBalanceAmount + expenseBalanceAmount + transferBalanceAmount);
  data.push({
    item: 'Saldo Inicial',
    value: formatCurrencyPdf(initialBalance),
    isHeader: true
  });

  // Income row
  data.push({
    item: 'Receitas',
    value: formatCurrencyPdf(incomeBalanceAmount),
    isHeader: true
  });

  const flattenCategories = (cats, depth = 1, filterFn = () => true, skipRoot = false, skipRootApplied = false) => {
    if (!cats || cats.length === 0) return;

    cats.forEach((cat) => {
      const hasChildren = cat.categories && cat.categories.length > 0;
      const netAmount = (cat.incomeAmount || 0) + (cat.expensesAmount || 0);
      const hasAmount = (cat.incomeAmount || 0) !== 0 || (cat.expensesAmount || 0) !== 0;
      const isRootCategory = depth === 1 && !skipRootApplied;
      const shouldSkip = skipRoot && isRootCategory;

      if (!hasAmount) {
        return;
      }

      if (filterFn(cat) && !shouldSkip) {
        data.push({
          item: cleanCategoryLabel(cat.category?.name || ''),
          value: formatCurrencyPdf(netAmount),
          isHeader: hasChildren,
          indent: depth
        });
      }

      if (hasChildren) {
        const nextDepth = shouldSkip ? depth : depth + 1;
        flattenCategories(cat.categories, nextDepth, filterFn, skipRoot, skipRootApplied || shouldSkip);
      }
    });
  };

  flattenCategories(categories, 1, (cat) => (cat.incomeAmount || 0) > 0, true);

  // Expenses row
  data.push({
    item: 'Despesas',
    value: formatCurrencyPdf(expenseBalanceAmount),
    isHeader: true
  });

  flattenCategories(categories, 1, (cat) => (cat.incomeAmount || 0) <= 0);

  // Transfers row
  data.push({
    item: 'Transferências',
    value: formatCurrencyPdf(transferBalanceAmount),
    isHeader: true
  });

  // Final balance row
  data.push({
    item: 'Saldo Final',
    value: formatCurrencyPdf(monthBalanceAmount),
    isHeader: true
  });

  return data;
};

export const PdfBalanceSheetDetailed = ({ dataBilling }) => {
  const balance = dataBilling?.balance;
  const balanceSheetData = buildPdfBalanceSheetData(balance);

  if (!balanceSheetData || balanceSheetData.length === 0) {
    return null;
  }

  const tableCellOptions = {
    includeLeftBorder: false,
    includeTopBorder: false,
    includeRightBorder: false,
    includeBottomBorder: false,
  };

  return (
    <View>
      <Table data={balanceSheetData}>
        <TableBody {...tableCellOptions}>
            <DataTableCell
                style={balanceSheetStyles.tableCell}
                weighting={0.60}
                getContent={(r) => {
                let cellStyle = {...balanceSheetStyles.tableCell, paddingLeft: '2mm'};
                
                if (r.indent === 1) {
                    cellStyle = balanceSheetStyles.tableCellIndent1;
                } else if (r.indent === 2) {
                    cellStyle = balanceSheetStyles.tableCellIndent2;
                } else if (r.indent && r.indent > 2) {
                    cellStyle = {
                        ...balanceSheetStyles.tableCell,
                        paddingLeft: `${8 + (r.indent - 1) * 4}mm`
                    };
                }

                if (r.isHeader) {
                    cellStyle = { ...cellStyle, fontWeight: 'bold', backgroundColor: lightGray };
                }
                
                return (
                    <Text style={{...cellStyle}}>
                    {r.item}
                    </Text>
                );
                }}
            />
            <DataTableCell
                style={balanceSheetStyles.tableCell}
                weighting={0.40}
                getContent={(r) => {
                  let cellStyle = {...balanceSheetStyles.tableCellRight, paddingRight: '2mm'};

                  if (r.isHeader) {
                      cellStyle = { ...cellStyle, fontWeight: 'bold', backgroundColor: lightGray };
                  }
                  
                  return (
                      <Text style={cellStyle}>
                      {r.value}
                      </Text>
                  );
                }}
            />
        </TableBody>
      </Table>
      <Text style={styles.disclaimer}>
        Os valores acima referem-se à conta ordinária principal e consideram os saldos na data de emissão desta fatura. Para maiores detalhes, acesse nossa plataforma on line
      </Text>
    </View>
  );
};
